import { LOREBOOK_VIEW_LIVE, useLorebookEntries } from "@next-platform/client-common-core";
import { Spinner } from "@next-platform/client-common-spicy-ui";

import { PageLayout, PageSize, PageVariant } from "@/components";
import PageStaticMetaTags from "@/components/common/PageStaticMetaTags";

import LorebookDetailContent from "./LorebookDetailContent";
import LorebookProfileHeader from "./LorebookProfileHeader";

export default function LorebookDetail({ id }: { id: string }) {
  // The profile always serves the last approved version, owner included: a staged edit under review
  // would be misleading next to an Attach CTA that attaches the approved one. The Edit
  // screen is the only surface that shows the pending content, and it omits this option.
  const { lorebook, entries, isLoadingFirstPage, isLoadingMore, hasMore, hasError, searchValue, setSearchValue, loadNextPage, reload } =
    useLorebookEntries(id, { view: LOREBOOK_VIEW_LIVE });

  const totalEntryCount = lorebook?.num_entries ?? entries.length;

  if (hasError && !lorebook) {
    return (
      <div className="flex justify-center items-center h-full">
        <PageStaticMetaTags pageId="notFound" prerenderStatusCode={404} />
      </div>
    );
  }

  if (isLoadingFirstPage && !lorebook) {
    return (
      <div className="flex justify-center items-center h-full">
        <Spinner className="mx-auto" />
      </div>
    );
  }

  if (!lorebook) {
    return null;
  }

  return (
    <div className="flex flex-col gap-lg">
      <LorebookProfileHeader lorebook={lorebook} />
      <PageLayout size={PageSize.Medium} variant={PageVariant.Modal}>
        <LorebookDetailContent
          entries={entries}
          totalEntryCount={totalEntryCount}
          searchValue={searchValue}
          onSearchChange={setSearchValue}
          onEndReached={loadNextPage}
          isLoadingFirstPage={isLoadingFirstPage}
          isLoadingMore={isLoadingMore}
          hasMore={hasMore}
          hasError={hasError}
          onRetry={reload}
          onRetryLoadMore={loadNextPage}
        />
      </PageLayout>
    </div>
  );
}
